import { Injectable, inject, NgZone, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthStore } from '../store/auth.store';
import { NotificationService } from './notification.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../shared/components/confirm-dialog/confirm-dialog.component';

const TIMEOUT_MS = 30 * 60 * 1000;     // 30 minutes
const WARNING_MS = 25 * 60 * 1000;     // 25 minutes

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private auth = inject(AuthStore);
  private dialog = inject(MatDialog);
  private notification = inject(NotificationService);
  private ngZone = inject(NgZone);

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private warningId: ReturnType<typeof setTimeout> | null = null;
  private warningDialogOpen = false;
  private events = ['mousedown', 'keypress', 'scroll', 'touchstart'];

  start(): void {
    if (!this.auth.isAuthenticated()) return;

    this.resetTimers();

    // Run event listeners outside Angular zone to avoid triggering change detection
    this.ngZone.runOutsideAngular(() => {
      this.events.forEach(event => {
        document.addEventListener(event, this.onActivity, { passive: true });
      });
    });
  }

  stop(): void {
    this.clearTimers();
    this.events.forEach(event => {
      document.removeEventListener(event, this.onActivity);
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private onActivity = (): void => {
    if (!this.warningDialogOpen) {
      this.resetTimers();
    }
  };

  private resetTimers(): void {
    this.clearTimers();

    this.warningId = setTimeout(() => {
      this.showWarning();
    }, WARNING_MS);

    this.timeoutId = setTimeout(() => {
      this.onTimeout();
    }, TIMEOUT_MS);
  }

  private clearTimers(): void {
    if (this.warningId) {
      clearTimeout(this.warningId);
      this.warningId = null;
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private showWarning(): void {
    if (this.warningDialogOpen) return;
    this.warningDialogOpen = true;

    this.ngZone.run(() => {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          title: 'Session expiring',
          message: 'Your session will expire in 5 minutes due to inactivity. Would you like to continue working?',
          confirmLabel: 'Continue session',
          cancelLabel: 'Sign out',
        } satisfies ConfirmDialogData,
      });

      dialogRef.afterClosed().subscribe(continueSession => {
        this.warningDialogOpen = false;
        if (continueSession) {
          this.resetTimers();
        } else {
          this.auth.logout();
          this.notification.showInfo('You have been signed out.');
        }
      });
    });
  }

  private onTimeout(): void {
    this.dialog.closeAll();
    this.warningDialogOpen = false;
    this.stop();
    this.ngZone.run(() => {
      this.auth.logout();
      this.notification.showWarning('Your session has expired due to inactivity.');
    });
  }
}
